import React, { useState } from 'react'
import { Card, CardContent, CardHeading } from '@components/layout/Card'
import { Button } from '@UILibrary/Button'
import Pill from '@UILibrary/Pill'

import { BackupCodes } from './components/BackupCodes'
import { EnrolmentPanel } from './components/EnrolmentPanel'
import {
  Enrolment,
  SecuritySettings as Settings,
  confirmEnrolment,
  disableAuthenticator,
  regenerateBackupCodes,
  startEnrolment,
} from './api'
import { LOW_BACKUP_CODES, backupCodesRemainingLabel, maskCellphoneNumber } from './utils'

import styles from './security-settings.module.sass'

export const SecuritySettings = (props: Settings) => {
  const [settings, setSettings] = useState<Settings>(props)
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const run = async (action: () => Promise<void>) => {
    setError(null)
    setIsSubmitting(true)

    try {
      await action()
    } catch (requestError) {
      setError((requestError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const beginEnrolment = () => run(async () => {
    setEnrolment(await startEnrolment(settings.paths))
  })

  const confirm = (code: string) => run(async () => {
    const updated = await confirmEnrolment(settings.paths, code)

    setSettings(updated)
    setBackupCodes(updated.backupCodes || [])
    setEnrolment(null)
  })

  const regenerate = () => run(async () => {
    const updated = await regenerateBackupCodes(settings.paths)

    setSettings(updated)
    setBackupCodes(updated.backupCodes || [])
  })

  const turnOff = () => run(async () => {
    setSettings(await disableAuthenticator(settings.paths))
    setBackupCodes(null)
  })

  const cancelEnrolment = () => {
    setEnrolment(null)
    setError(null)
  }

  return (
    <div className={styles.securitySettings}>
      <h1>Security</h1>

      <section>
        <h2>Two-factor authentication</h2>
        <p>Each time you sign in, we ask for a one-time code from one of these.</p>

        <SmsCard settings={settings} />

        <Card>
          <MethodHeading title="Authenticator app" status={authenticatorStatus(settings)} />
          <CardContent>
            <div key={cardState(backupCodes, enrolment, settings)} className={styles.cardTransition}>
              {renderBody({
                backupCodes,
                beginEnrolment,
                confirm,
                cancelEnrolment,
                enrolment,
                error,
                isSubmitting,
                regenerate,
                settings,
                turnOff,
                dismissBackupCodes: () => setBackupCodes(null),
              })}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}

type BodyProps = {
  backupCodes: string[] | null
  beginEnrolment: () => void
  cancelEnrolment: () => void
  confirm: (code: string) => void
  dismissBackupCodes: () => void
  enrolment: Enrolment | null
  error: string | null
  isSubmitting: boolean
  regenerate: () => void
  settings: Settings
  turnOff: () => void
}

// Keys the card body, so moving between states remounts it and replays the
// entrance animation instead of swapping the contents in place.
const cardState = (backupCodes: string[] | null, enrolment: Enrolment | null, settings: Settings) => {
  if (backupCodes) return 'backup-codes'
  if (enrolment) return 'enrolment'

  return settings.authenticatorEnabled ? 'on' : 'off'
}

// The card is one of four things at a time — freshly issued codes, a setup in
// progress, an authenticator already on, or the SMS-only default. Choosing here
// rather than in the JSX keeps the four states readable side by side.
const renderBody = (props: BodyProps) => {
  if (props.backupCodes) {
    return <BackupCodes codes={props.backupCodes} onDismiss={props.dismissBackupCodes} />
  }

  if (props.enrolment) {
    return (
      <EnrolmentPanel
        enrolment={props.enrolment}
        error={props.error}
        isSubmitting={props.isSubmitting}
        onCancel={props.cancelEnrolment}
        onConfirm={props.confirm}
      />
    )
  }

  if (props.settings.authenticatorEnabled) {
    return <AuthenticatorOn {...props} />
  }

  return <AuthenticatorOff {...props} />
}

const AuthenticatorOn = ({ error, isSubmitting, regenerate, settings, turnOff }: BodyProps) => {
  const [isConfirmingTurnOff, setIsConfirmingTurnOff] = useState(false)

  const onAskToTurnOff = () => setIsConfirmingTurnOff(true)
  const onKeepOn = () => setIsConfirmingTurnOff(false)

  const errorMessage = error ? <p className={styles.error} role="alert">{error}</p> : null
  const isLowOnBackupCodes = settings.backupCodesRemaining < LOW_BACKUP_CODES
  const backupCodesWarning = isLowOnBackupCodes
    ? <p className={styles.warning}>You are running low. Generate a new set so you are not locked out.</p>
    : null

  const actions = isConfirmingTurnOff
    ? (
      <div className={`${styles.panel} ${styles.confirmTurnOff}`}>
        <p>
          Turn off the authenticator app? You will get your sign-in code by SMS again, your backup
          codes will stop working, and you can delete the Wealthbit entry from your app.
        </p>
        <div className={styles.actions}>
          <Button variant="secondary" disabled={isSubmitting} onClick={onKeepOn}>Keep it on</Button>
          <Button variant="destructive" disabled={isSubmitting} onClick={turnOff}>Turn off</Button>
        </div>
      </div>
    )
    : (
      <div className={styles.actions}>
        <Button variant="secondary" disabled={isSubmitting} onClick={regenerate}>
          Generate new backup codes
        </Button>
        <Button variant="secondary" disabled={isSubmitting} onClick={onAskToTurnOff}>
          Turn off
        </Button>
      </div>
    )

  return (
    <div className={styles.panel}>
      <p>We ask for the code from your authenticator app when you sign in.</p>

      <div className={styles.detail}>
        <span className={styles.detailLabel}>Backup codes</span>
        <div className={styles.detailValue}>
          <p>
            {backupCodesRemainingLabel(settings.backupCodesRemaining)}. Each works once if you cannot reach
            your app.
          </p>
          {backupCodesWarning}
        </div>
      </div>
      {errorMessage}
      {actions}
    </div>
  )
}

const AuthenticatorOff = ({ beginEnrolment, error, isSubmitting }: BodyProps) => {
  const errorMessage = error ? <p className={styles.error} role="alert">{error}</p> : null

  return (
    <div className={styles.panel}>
      <p>
        Get your sign-in code from an app like Authy, Google Authenticator or 1Password instead of
        waiting for an SMS. It works without signal, and setup takes about a minute.
      </p>
      {errorMessage}

      <div className={styles.actions}>
        <Button variant="primary" disabled={isSubmitting} onClick={beginEnrolment}>
          {isSubmitting ? 'Setting up…' : 'Set up an authenticator app'}
        </Button>
      </div>
    </div>
  )
}

// SMS cannot be switched off: it is how every profile starts, and it stays the
// fallback behind "Send a code by SMS instead" once an authenticator is on. So
// the card reports which role it plays rather than offering a toggle.
const SmsCard = ({ settings }: { settings: Settings }) => {
  const number = maskCellphoneNumber(settings.cellphoneNumber)
  const description = settings.authenticatorEnabled
    ? `Backup only. If you cannot reach your authenticator app, choose "Send a code by SMS instead" when you sign in and we text ${number}.`
    : `We text your sign-in code to ${number}.`

  return (
    <Card>
      <MethodHeading title="SMS" status={smsStatus(settings)} />
      <CardContent>
        <div className={styles.panel}>
          <p>{description}</p>
        </div>
      </CardContent>
    </Card>
  )
}

type MethodStatus = {
  color: 'info' | 'success'
  label: string
}

const smsStatus = (settings: Settings): MethodStatus =>
  settings.authenticatorEnabled ? { color: 'info', label: 'Backup' } : { color: 'success', label: 'On' }

const authenticatorStatus = (settings: Settings): MethodStatus =>
  settings.authenticatorEnabled ? { color: 'success', label: 'On' } : { color: 'info', label: 'Off' }

type MethodHeadingProps = {
  status: MethodStatus
  title: string
}

const MethodHeading = ({ status, title }: MethodHeadingProps) => (
  <CardHeading className={styles.methodHeading}>
    <h5>{title}</h5>
    <Pill color={status.color} size="small">{status.label}</Pill>
  </CardHeading>
)
